/**
 * M2 onboarding: logo picker — choose from website/EIN/upload options, with lightbox.
 */

import React, { useState, useEffect } from 'react';

const EYE_SVG = (
  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

export default function LogoPicker({
  imageOptions = [],
  selectedUrl,
  onSelect,
  onUploadClick,
  uploadButtonLabel = 'Upload my own',
}) {
  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    if (!lightboxSrc) return;
    const onEscape = (e) => { if (e.key === 'Escape') setLightboxSrc(null); };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [lightboxSrc]);

  return (
    <div className="flex flex-col gap-2 ">
      <div className="flex flex-col gap-1">
        <label className="text-base font-medium text-ink-secondary">Logo</label>
        <p className="text-xs text-ink-muted">
          Pick one to use as your logo, or upload your own
        </p>
      </div>
      <div className="flex items-center gap-4 pl-1 flex-wrap">
        {imageOptions.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onSelect(opt.url)}
            className={`shrink-0 w-28 h-28 rounded-lg border-2 bg-paper-inset overflow-hidden flex items-center justify-center relative focus:outline-none focus:ring-2 focus:ring-sage focus:ring-offset-1 transition-all duration-200 ease-out ${selectedUrl === opt.url ? 'border-sage ring-2 ring-sage ring-offset-1' : 'border-edge hover:border-ink-muted'}`}
            title={opt.title}
          >
            <img src={opt.url} alt={opt.alt} className="w-full h-full object-contain" />
            <span
              onClick={(e) => {
                e.stopPropagation();
                setLightboxSrc(opt.url);
              }}
              className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"
              title="View full size"
              aria-label="View full size"
            >
              {EYE_SVG}
            </span>
          </button>
        ))}
        {onUploadClick && (
          <button
            type="button"
            onClick={onUploadClick}
            className="shrink-0 w-28 h-28 rounded-lg border-2 border-dashed border-edge hover:border-ink-muted bg-paper-inset flex flex-col items-center justify-center gap-1 text-center text-ink-muted hover:text-ink focus:outline-none focus:ring-2 focus:ring-sage focus:ring-offset-1 transition-all duration-200 ease-out"
            title={uploadButtonLabel}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <span className="text-sm font-medium px-1">{uploadButtonLabel}</span>
          </button>
        )}
      </div>
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setLightboxSrc(null)}
          role="button"
          tabIndex={0}
          aria-label="Close full size image"
        >
          <div
            className="bg-paper-light rounded-lg p-4 max-w-full max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxSrc}
              alt="Logo full size"
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>
          <button
            type="button"
            onClick={() => setLightboxSrc(null)}
            className="absolute right-4 top-4 rounded-full bg-paper-light/90 p-2 text-ink shadow hover:bg-paper-light"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
